import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Heart, Loader2, Send, ShieldCheck } from "lucide-react";
import { LogoMark } from "@/components/LogoMark";
import { cercoLabel } from "@/data/formOptions";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const time = (iso) => new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });

const Bubble = ({ m, mine }) => (
  <div className={`flex ${mine ? "justify-end" : "justify-start"}`}>
    <div
      className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
        mine
          ? "bg-[#0A192F] text-white rounded-br-md"
          : "bg-white border border-slate-200 text-[#0A192F] rounded-bl-md"
      }`}
    >
      <p className="whitespace-pre-wrap">{m.text}</p>
      <p className={`text-[10px] mt-1 ${mine ? "text-slate-400" : "text-slate-400"}`}>{time(m.created_at)}</p>
    </div>
  </div>
);

export default function Match() {
  const params = new URLSearchParams(window.location.search);
  const unlockId = params.get("u") || "";
  const requestId = params.get("r") || "";
  const reqKey = params.get("k") || "";
  const side = unlockId ? "buyer" : "requester";
  const token = unlockId || reqKey;

  const [data, setData] = useState(null);
  const [activeChat, setActiveChat] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const bottomRef = useRef(null);

  const load = async () => {
    try {
      const url = unlockId
        ? `${API}/chat/by-unlock/${unlockId}`
        : `${API}/chat/by-request/${requestId}?k=${reqKey}`;
      const { data } = await axios.get(url);
      setData(data);
      if (data.side === "buyer") {
        setActiveChat({ chat_id: data.chat_id, messages: data.messages });
      } else {
        setActiveChat((cur) => {
          if (cur) {
            const found = data.chats.find((c) => c.chat_id === cur.chat_id);
            return found || (data.chats[0] || null);
          }
          return data.chats[0] || null;
        });
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Link non valido o scaduto");
    }
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 4000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [activeChat?.messages?.length]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || !activeChat) return;
    setSending(true);
    try {
      await axios.post(`${API}/chat/${activeChat.chat_id}/send?side=${side}&token=${token}`, { text: text.trim() });
      setText("");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invio non riuscito");
    } finally {
      setSending(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center px-5">
        <div className="text-center bg-white rounded-3xl border border-slate-200 p-10 max-w-sm">
          <LogoMark size={48} />
          <p className="font-display font-bold text-[#0A192F] mt-5" data-testid="match-error">{error}</p>
          <a href="/" className="inline-block mt-4 text-sm font-bold text-[#10B981] hover:underline">← Torna alla home</a>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#10B981]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex flex-col" data-testid="match-page">
      <header className="bg-[#0A192F] text-white">
        <div className="max-w-3xl mx-auto px-5 py-4 flex items-center gap-3">
          <LogoMark size={36} />
          <div className="leading-tight">
            <p className="font-display font-extrabold flex items-center gap-1.5">
              Match <Heart className="w-4 h-4 fill-[#10B981] text-[#10B981]" />
            </p>
            <p className="text-[11px] text-slate-400">
              {side === "buyer"
                ? data.request ? `${cercoLabel(data.request.tipo_ricerca)} · ${data.request.comune} (${data.request.provincia}) · € ${Number(data.request.budget_max).toLocaleString("it-IT")}` : "Conversazione"
                : `Le tue conversazioni · ${data.nome}`}
            </p>
          </div>
          <span className="ml-auto inline-flex items-center gap-1.5 text-[11px] font-semibold text-emerald-300">
            <ShieldCheck className="w-3.5 h-3.5" /> Contatti protetti
          </span>
        </div>
      </header>

      <div className="flex-1 max-w-3xl w-full mx-auto px-5 py-6 flex flex-col">
        {side === "requester" && data.chats.length > 1 && (
          <div className="flex gap-2 mb-4 overflow-x-auto pb-1" data-testid="match-chat-list">
            {data.chats.map((c, i) => (
              <button
                key={c.chat_id}
                onClick={() => setActiveChat(c)}
                data-testid={`match-chat-tab-${i}`}
                className={`shrink-0 rounded-full px-4 py-2 text-xs font-bold transition-colors ${
                  activeChat?.chat_id === c.chat_id ? "bg-[#0A192F] text-white" : "bg-white border border-slate-200 text-slate-600"
                }`}
              >
                Match #{data.chats.length - i} · {c.buyer}
              </button>
            ))}
          </div>
        )}

        {!activeChat ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center py-20">
            <Heart className="w-12 h-12 text-slate-200 mb-4" />
            <p className="font-display font-bold text-[#0A192F]" data-testid="match-empty">Nessun Match ancora</p>
            <p className="text-sm text-slate-500 mt-2 max-w-xs leading-relaxed">
              Quando qualcuno sblocca la tua richiesta e ti scrive, la conversazione appare qui.
            </p>
          </div>
        ) : (
          <>
            <div className="flex-1 space-y-3 py-2" data-testid="match-messages">
              {activeChat.messages.length === 0 && (
                <p className="text-center text-xs text-slate-400 py-10">
                  {side === "buyer"
                    ? "È scattato il Match! Scrivi il primo messaggio: presentati e proponi l'immobile."
                    : "La conversazione è pronta: rispondi quando vuoi."}
                </p>
              )}
              {activeChat.messages.map((m) => (
                <Bubble key={m.id} m={m} mine={m.sender === side} />
              ))}
              <div ref={bottomRef} />
            </div>

            <form onSubmit={send} className="sticky bottom-4 mt-4">
              <div className="flex items-center gap-2.5 bg-white rounded-full border border-slate-200 shadow-[0_10px_30px_rgba(10,25,47,0.1)] p-2 pl-5">
                <input
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  data-testid="match-input"
                  placeholder={side === "buyer" ? "Scrivi al richiedente..." : "Scrivi la tua risposta..."}
                  className="flex-1 bg-transparent outline-none text-sm text-[#0A192F] placeholder:text-slate-400"
                  maxLength={2000}
                />
                <button
                  type="submit"
                  disabled={sending || !text.trim()}
                  data-testid="match-send"
                  className="w-11 h-11 rounded-full bg-[#10B981] text-white flex items-center justify-center hover:bg-[#059669] transition-colors disabled:opacity-50 shrink-0"
                >
                  {sending ? <Loader2 className="w-4.5 h-4.5 animate-spin" /> : <Send className="w-4.5 h-4.5" />}
                </button>
              </div>
              {side === "requester" && (
                <p className="text-[11px] text-slate-400 text-center mt-2.5">
                  Il tuo numero resta nascosto: condividilo solo quando ti fidi.
                </p>
              )}
            </form>
          </>
        )}
      </div>
    </div>
  );
}
